import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe, Plus, ChevronLeft, ChevronRight, Zap, Clock, X, Loader2, Key, Copy, CheckCircle2, Pencil, Search, Download } from 'lucide-react';
import { api } from '../lib/api';
import { exportCsv } from '../lib/exportCsv';
import { useAuthStore } from '../store/auth.store';

const inputCls = 'w-full bg-[#0b1120] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-cyan-500/40 transition-colors placeholder-slate-700';

// Fetches the last 10 WSF history points and renders a 60×20 inline sparkline
function WsfSparkline({ worldId, currentWsf }: { worldId: string; currentWsf: number | null }) {
  const { data } = useQuery<{ wsfValue: number; calculatedAt: string }[]>({
    queryKey: ['wsf-history', worldId],
    queryFn: () => api.get(`/analytics/worlds/${worldId}/wsf-history`).then((r) => r.data),
    staleTime: 5 * 60 * 1000,
  });

  const points = (data ?? []).slice().reverse().slice(-10);
  const W = 60; const H = 20;

  let polyline: string | null = null;
  if (points.length >= 2) {
    const vals  = points.map((p) => Number(p.wsfValue));
    const minV  = Math.min(...vals);
    const maxV  = Math.max(...vals);
    const range = maxV - minV || 0.01;
    polyline = points
      .map((p, i) => {
        const x = (i / (points.length - 1)) * W;
        const y = H - ((Number(p.wsfValue) - minV) / range) * (H - 2) - 1;
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(' ');
  }

  return (
    <div className="flex items-center gap-2">
      <Zap className="w-3 h-3 text-amber-400 flex-shrink-0" />
      <span className="text-slate-300 font-mono text-sm">{currentWsf ?? '—'}</span>
      {polyline && (
        <svg width={W} height={H} className="opacity-60">
          <polyline points={polyline} fill="none" stroke="#fbbf24" strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
        </svg>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const active = status === 'ACTIVE';
  const pending = status === 'PENDING';
  const cls = active
    ? 'badge-active'
    : pending
    ? 'bg-amber-500/15 border border-amber-500/25 text-amber-400'
    : 'badge-inactive';
  const dotCls = active ? 'bg-emerald-400 pulse-dot' : pending ? 'bg-amber-400' : 'bg-slate-500';
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium ${cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${dotCls}`} />
      {status}
    </span>
  );
}

function SkeletonRow() {
  return (
    <tr>
      {Array.from({ length: 6 }).map((_, i) => (
        <td key={i} className="px-5 py-4">
          <div className="shimmer h-4 rounded w-3/4" />
        </td>
      ))}
    </tr>
  );
}

interface CreatedWorld {
  id: string;
  name: string;
  apiKey: string;
  hmacSecret: string;
}

// ─── Edit World Modal ────────────────────────────────────────────────────────

function EditWorldModal({ world, onClose }: { world: Record<string, unknown>; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(String(world['name'] ?? ''));
  const [currencyName, setCurrencyName] = useState(String(world['currencyName'] ?? ''));
  const [currencySymbol, setCurrencySymbol] = useState(String(world['currencySymbol'] ?? ''));
  const [revenueShare, setRevenueShare] = useState(String(world['revenueShare'] ?? '0'));
  const [taxShare, setTaxShare] = useState(String(world['taxShare'] ?? '0'));
  const [taxOwnerId, setTaxOwnerId] = useState(String(world['taxOwnerId'] ?? ''));

  const { data: holders = [] } = useQuery<{ id: string; name: string; email: string }[]>({
    queryKey: ['holders', world['id']],
    queryFn: () => api.get(`/holders?worldId=${world['id']}`).then((r) => r.data),
  });

  const mutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.patch(`/worlds/${world['id']}`, data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['worlds'] });
      onClose();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate({
      name: name.trim(),
      currencyName: currencyName.trim(),
      currencySymbol: currencySymbol.trim().toUpperCase(),
      revenueShare: Math.min(100, Math.max(0, parseFloat(revenueShare) || 0)),
      taxShare: Math.min(100, Math.max(0, parseFloat(taxShare) || 0)),
      taxOwnerId: taxOwnerId || '',
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 12 }}
        transition={{ type: 'spring', stiffness: 380, damping: 30 }}
        className="relative w-full max-w-lg glass-card p-6 z-10 overflow-y-auto max-h-[90vh]"
        style={{ borderColor: 'rgba(0,212,255,0.2)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute top-0 left-0 right-0 h-px rounded-t-2xl bg-gradient-to-r from-transparent via-cyan-500/40 to-transparent" />

        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
              <Pencil className="w-4 h-4 text-cyan-400" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-white">Edit World</h3>
              <p className="text-[10px] text-slate-600 font-mono">{String(world['id']).slice(0, 12)}…</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-600 hover:text-slate-300 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Identity section */}
          <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-600">Identity</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">World Name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} required minLength={3} maxLength={64} className={inputCls} />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">Currency Name</label>
              <input value={currencyName} onChange={(e) => setCurrencyName(e.target.value)} required minLength={2} maxLength={50} className={inputCls} />
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">Currency Symbol</label>
            <input
              value={currencySymbol}
              onChange={(e) => setCurrencySymbol(e.target.value.toUpperCase().replace(/[^A-Z]/g, ''))}
              required
              minLength={2}
              maxLength={10}
              pattern="[A-Z]{2,10}"
              className={`${inputCls} font-mono tracking-widest`}
            />
          </div>

          {/* Financial rules section */}
          <div className="pt-2 border-t border-white/5">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-3">Financial Rules</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">
                  Revenue Share %
                  <span className="normal-case tracking-normal text-slate-700 ml-1">→ CB in UKC</span>
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={revenueShare}
                    onChange={(e) => setRevenueShare(e.target.value)}
                    min="0"
                    max="100"
                    step="0.0001"
                    className={inputCls}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-600">%</span>
                </div>
                <p className="text-[10px] text-slate-700 mt-1">% of every credit sent to Central Bank (after exchange rate)</p>
              </div>
              <div>
                <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">
                  Tax Share %
                  <span className="normal-case tracking-normal text-slate-700 ml-1">→ tax owner</span>
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={taxShare}
                    onChange={(e) => setTaxShare(e.target.value)}
                    min="0"
                    max="100"
                    step="0.0001"
                    className={inputCls}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-600">%</span>
                </div>
                <p className="text-[10px] text-slate-700 mt-1">% after revenue share credited to tax owner in world currency</p>
              </div>
            </div>
          </div>

          {/* Tax owner section */}
          <div>
            <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">Tax Owner</label>
            <select
              value={taxOwnerId}
              onChange={(e) => setTaxOwnerId(e.target.value)}
              className={`${inputCls} cursor-pointer`}
            >
              <option value="">— None (no tax collection) —</option>
              {holders.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.name} ({h.email})
                </option>
              ))}
            </select>
            {holders.length === 0 && (
              <p className="text-[10px] text-slate-700 mt-1">No holders registered for this world yet</p>
            )}
          </div>

          {mutation.isError && (
            <p className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2">
              {(mutation.error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed to update world.'}
            </p>
          )}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl text-sm text-slate-400 border border-white/8 hover:border-white/15 hover:text-slate-300 transition-all">
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="flex-1 btn-primary flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold"
            >
              {mutation.isPending ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</> : 'Save Changes'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

function RegisterWorldModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [currencyName, setCurrencyName] = useState('');
  const [currencySymbol, setCurrencySymbol] = useState('');
  const [revenueShare, setRevenueShare] = useState('0');
  const [taxShare, setTaxShare] = useState('0');
  const [taxOwnerId, setTaxOwnerId] = useState('');
  const [created, setCreated] = useState<CreatedWorld | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const { data: allHolders = [] } = useQuery<{ id: string; name: string; email: string }[]>({
    queryKey: ['holders-all'],
    queryFn: () => api.get('/holders').then((r) => r.data),
  });

  const mutation = useMutation({
    mutationFn: (data: { name: string; currencyName: string; currencySymbol: string; revenueShare: number; taxShare: number; taxOwnerId?: string }) =>
      api.post('/worlds', data).then((r) => r.data),
    onSuccess: (data: CreatedWorld) => {
      setCreated(data);
      queryClient.invalidateQueries({ queryKey: ['worlds'] });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate({
      name: name.trim(),
      currencyName: currencyName.trim(),
      currencySymbol: currencySymbol.trim().toUpperCase(),
      revenueShare: Math.min(100, Math.max(0, parseFloat(revenueShare) || 0)),
      taxShare: Math.min(100, Math.max(0, parseFloat(taxShare) || 0)),
      ...(taxOwnerId ? { taxOwnerId } : {}),
    });
  };

  const copyToClipboard = (val: string, key: string) => {
    navigator.clipboard.writeText(val);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 12 }}
        transition={{ type: 'spring', stiffness: 380, damping: 30 }}
        className="relative w-full max-w-lg glass-card p-6 z-10 overflow-y-auto max-h-[90vh]"
        style={{ borderColor: 'rgba(0,212,255,0.2)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top gradient */}
        <div className="absolute top-0 left-0 right-0 h-px rounded-t-2xl bg-gradient-to-r from-transparent via-cyan-500/40 to-transparent" />

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
              <Globe className="w-4 h-4 text-cyan-400" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-white">Register World</h3>
              <p className="text-[10px] text-slate-600">Create a new game world</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-600 hover:text-slate-300 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Success — show API key / HMAC secret once */}
        {created ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-emerald-400 text-sm font-medium mb-2">
              <CheckCircle2 className="w-4 h-4" />
              World "{created.name}" created
            </div>
            <p className="text-xs text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-xl px-3 py-2.5">
              Save these credentials now — they are shown <strong>only once</strong>.
            </p>

            {[
              { label: 'API Key', value: created.apiKey, key: 'apiKey' },
              { label: 'HMAC Secret', value: created.hmacSecret, key: 'hmacSecret' },
            ].map(({ label, value, key }) => (
              <div key={key}>
                <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">{label}</p>
                <div className="flex items-center gap-2 bg-[#0b1120] border border-white/8 rounded-xl px-3 py-2.5">
                  <Key className="w-3.5 h-3.5 text-slate-600 flex-shrink-0" />
                  <span className="font-mono text-xs text-cyan-300 break-all flex-1">{value}</span>
                  <button
                    onClick={() => copyToClipboard(value, key)}
                    className="flex-shrink-0 text-slate-600 hover:text-cyan-400 transition-colors"
                  >
                    {copied === key ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
            ))}

            <button
              onClick={onClose}
              className="w-full btn-primary py-2.5 rounded-xl text-sm font-semibold mt-2"
            >
              Done
            </button>
          </div>
        ) : (
          /* Registration form */
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">World Name</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Aetheria"
                required
                minLength={3}
                maxLength={64}
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">Currency Name</label>
              <input
                value={currencyName}
                onChange={(e) => setCurrencyName(e.target.value)}
                placeholder="e.g. Gold Coins"
                required
                minLength={2}
                maxLength={50}
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">
                Currency Symbol <span className="text-slate-700 normal-case tracking-normal">(2–10 uppercase letters)</span>
              </label>
              <input
                value={currencySymbol}
                onChange={(e) => setCurrencySymbol(e.target.value.toUpperCase().replace(/[^A-Z]/g, ''))}
                placeholder="e.g. GCOINS"
                required
                minLength={2}
                maxLength={10}
                pattern="[A-Z]{2,10}"
                className={`${inputCls} font-mono tracking-widest`}
              />
            </div>

            {/* Financial rules */}
            <div className="pt-2 border-t border-white/5">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-3">Financial Rules</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">
                    Revenue Share %
                    <span className="normal-case tracking-normal text-slate-700 ml-1">→ CB in UKC</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={revenueShare}
                      onChange={(e) => setRevenueShare(e.target.value)}
                      min="0"
                      max="100"
                      step="0.0001"
                      className={inputCls}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-600">%</span>
                  </div>
                  <p className="text-[10px] text-slate-700 mt-1">% of every credit sent to Central Bank after exchange rate</p>
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">
                    Tax Share %
                    <span className="normal-case tracking-normal text-slate-700 ml-1">→ tax owner</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={taxShare}
                      onChange={(e) => setTaxShare(e.target.value)}
                      min="0"
                      max="100"
                      step="0.0001"
                      className={inputCls}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-600">%</span>
                  </div>
                  <p className="text-[10px] text-slate-700 mt-1">% after revenue share credited to tax owner in world currency</p>
                </div>
              </div>
              <p className="text-[10px] text-slate-700 mt-2">Tax owner can be updated at any time from the edit modal.</p>
            </div>

            {/* Tax owner */}
            <div>
              <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">Tax Owner</label>
              <select
                value={taxOwnerId}
                onChange={(e) => setTaxOwnerId(e.target.value)}
                className={`${inputCls} cursor-pointer`}
              >
                <option value="">— None (no tax collection) —</option>
                {allHolders.map((h) => (
                  <option key={h.id} value={h.id}>
                    {h.name} ({h.email})
                  </option>
                ))}
              </select>
              {allHolders.length === 0 && (
                <p className="text-[10px] text-slate-700 mt-1">No holders in the system yet — assign after creating holders.</p>
              )}
            </div>

            {mutation.isError && (
              <p className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2">
                {(mutation.error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed to create world. Please try again.'}
              </p>
            )}

            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl text-sm text-slate-400 border border-white/8 hover:border-white/15 hover:text-slate-300 transition-all">
                Cancel
              </button>
              <button
                type="submit"
                disabled={mutation.isPending}
                className="flex-1 btn-primary flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold"
              >
                {mutation.isPending ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Creating...</> : <><Plus className="w-3.5 h-3.5" /> Create</>}
              </button>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
}

export function WorldsPage() {
  const [showModal, setShowModal] = useState(false);
  const [editWorld, setEditWorld] = useState<Record<string, unknown> | null>(null);
  const role = useAuthStore((s) => s.role);
  const isPartner = role === 'WORLD_OWNER';
  const isReadOnly = role === 'ADMIN_READONLY';
  const { data: worlds = [], isLoading } = useQuery({
    queryKey: ['worlds'],
    queryFn: () => api.get('/worlds').then((r) => r.data),
  });

  const WORLDS_PER_PAGE = 25;
  const [search, setSearch] = useState('');
  const filteredWorlds = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q
      ? worlds.filter((w: Record<string, string>) =>
          (w['name'] ?? '').toLowerCase().includes(q) ||
          (w['currencySymbol'] ?? '').toLowerCase().includes(q))
      : worlds;
  }, [worlds, search]);
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(filteredWorlds.length / WORLDS_PER_PAGE));
  const paginatedWorlds = useMemo(
    () => filteredWorlds.slice((page - 1) * WORLDS_PER_PAGE, page * WORLDS_PER_PAGE),
    [filteredWorlds, page],
  );

  return (
    <div className="p-8">
      {/* Register World Modal */}
      <AnimatePresence>
        {showModal && <RegisterWorldModal onClose={() => setShowModal(false)} />}
        {editWorld && <EditWorldModal world={editWorld} onClose={() => setEditWorld(null)} />}
      </AnimatePresence>

      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-xl font-bold text-white">Worlds</h2>
          <p className="text-sm text-slate-600 mt-0.5">{worlds.length} {isPartner ? 'assigned worlds' : 'registered worlds'}</p>
        </div>
        {!isPartner && !isReadOnly && (
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 btn-primary text-xs font-semibold px-4 py-2 rounded-xl"
          >
            <Plus className="w-3.5 h-3.5" />
            Register World
          </button>
        )}
        <button
          onClick={() => exportCsv(
            filteredWorlds.map((w: Record<string, unknown>) => w),
            `worlds-${new Date().toISOString().slice(0, 10)}.csv`,
            [
              { key: 'id',            label: 'World ID' },
              { key: 'name',          label: 'Name' },
              { key: 'currencySymbol', label: 'Symbol' },
              { key: 'currencyName',  label: 'Currency' },
              { key: 'status',        label: 'Status' },
              { key: 'currentWsf',    label: 'WSF Factor' },
              { key: 'createdAt',     label: 'Created At' },
            ],
          )}
          className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-cyan-300 transition-colors px-3 py-2 rounded-xl border border-white/[0.06] hover:border-cyan-500/20 bg-white/[0.02]"
        >
          <Download className="w-3.5 h-3.5" /> Export CSV
        </button>
      </div>

      {/* Table */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="glass-card overflow-hidden"
      >
        {/* Top highlight */}
        <div className="h-px bg-gradient-to-r from-transparent via-cyan-500/25 to-transparent" />

        {/* Search bar */}
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/[0.04]">
          <Search className="w-3.5 h-3.5 text-slate-600 flex-shrink-0" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search by name or currency…"
            className="flex-1 bg-transparent text-sm text-slate-300 placeholder-slate-700 focus:outline-none"
          />
          {search && (
            <button onClick={() => { setSearch(''); setPage(1); }} className="text-slate-600 hover:text-slate-400">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <table className="w-full text-sm">
          <thead>
            <tr style={{ borderBottom: '1px solid rgba(0,212,255,0.08)' }}>
              {['World', 'Currency', 'Status', 'WSF Factor', 'Created', ''].map((h) => (
                <th key={h} className="text-left px-5 py-3.5 text-[10px] font-semibold uppercase tracking-widest text-slate-600">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 4 }).map((_, i) => <SkeletonRow key={i} />)
            ) : filteredWorlds.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-5 py-16 text-center">
                  <Globe className="w-10 h-10 text-slate-700 mx-auto mb-3" />
                  <p className="text-slate-500 text-sm">{search ? 'No worlds match your search' : 'No worlds registered yet'}</p>
                  {!search && <p className="text-slate-700 text-xs mt-1">Use the Register World button to get started</p>}
                </td>
              </tr>
            ) : (
              paginatedWorlds.map((w: Record<string, string>, idx: number) => (
                <motion.tr
                  key={w['id']}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.05, duration: 0.3 }}
                  className="table-row-hover"
                  style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}
                >
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500/15 to-purple-500/15 border border-cyan-500/15 flex items-center justify-center">
                        <Globe className="w-3.5 h-3.5 text-cyan-400/70" />
                      </div>
                      <div>
                        <div className="font-semibold text-white text-sm">{w['name']}</div>
                        <div className="font-mono text-[10px] text-slate-700">{w['id']?.slice(0, 8)}…</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm text-slate-300 bg-white/[0.04] px-2 py-0.5 rounded">
                        {w['currencySymbol'] ?? '—'}
                      </span>
                      <span className="text-xs text-slate-600">{w['currencyName']}</span>
                    </div>
                  </td>
                  <td className="px-5 py-4"><StatusBadge status={w['status'] ?? 'INACTIVE'} /></td>
                  <td className="px-5 py-4">
                    <WsfSparkline worldId={w['id']} currentWsf={w['currentWsf'] ?? null} />
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-1.5 text-slate-600 text-xs">
                      <Clock className="w-3 h-3" />
                      {w['createdAt'] ? new Date(w['createdAt']).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                    </div>
                  </td>
                  <td className="px-5 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {!isPartner && !isReadOnly && (
                        <button
                          onClick={() => setEditWorld(w)}
                          className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-cyan-300 transition-colors font-medium px-2 py-1 rounded-lg border border-white/5 hover:border-cyan-500/15 bg-white/[0.02]"
                        >
                          <Pencil className="w-3 h-3" /> Edit
                        </button>
                      )}
                      <Link
                        to={`/worlds/${w['id']}`}
                        className="inline-flex items-center gap-1 text-xs text-cyan-500 hover:text-cyan-300 transition-colors font-medium"
                      >
                        View <ChevronRight className="w-3.5 h-3.5" />
                      </Link>
                    </div>
                  </td>
                </motion.tr>
              ))
            )}
          </tbody>
        </table>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-white/[0.04]">
            <span className="text-xs text-slate-600">
              Page {page} of {totalPages} &bull; {filteredWorlds.length}{search ? ` / ${worlds.length}` : ''} worlds
            </span>
            <div className="flex gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs text-slate-400 border border-white/[0.06] disabled:opacity-30 hover:border-white/10 transition-colors"
              >
                <ChevronLeft className="w-3.5 h-3.5" /> Prev
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs text-slate-400 border border-white/[0.06] disabled:opacity-30 hover:border-white/10 transition-colors"
              >
                Next <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}

